/**
 * Per-manufacturer revision terminology (shared-types v1.142.0
 * `MovementManufacturer.revisionTermSingular` / `revisionTermPlural`).
 *
 * Seiko calls its revisions "Revision Codes", ETA "Revisions", others
 * "Generations". Display copy only; absent means "Revision" / "Revisions".
 * Pure; the audit script imports the suggestion rules too.
 */

export interface RevisionTerms {
  singular: string;
  plural: string;
}

export const DEFAULT_REVISION_TERMS: RevisionTerms = { singular: 'Revision', plural: 'Revisions' };

/** The editor's preset pairs; anything else is Custom. */
export const REVISION_TERM_PRESETS: readonly RevisionTerms[] = [
  DEFAULT_REVISION_TERMS,
  { singular: 'Revision Code', plural: 'Revision Codes' },
  { singular: 'Generation', plural: 'Generations' },
];

/** The terms to show for a manufacturer: its own, each falling back to the default. */
export function revisionTerms(m?: { revisionTermSingular?: string; revisionTermPlural?: string } | null): RevisionTerms {
  return {
    singular: m?.revisionTermSingular?.trim() || DEFAULT_REVISION_TERMS.singular,
    plural: m?.revisionTermPlural?.trim() || DEFAULT_REVISION_TERMS.plural,
  };
}

/** The preset a stored pair matches, or -1 for a custom pair. Absent = the default preset. */
export function presetIndex(terms: RevisionTerms): number {
  return REVISION_TERM_PRESETS.findIndex((p) => p.singular === terms.singular && p.plural === terms.plural);
}

const SEIKO = /seiko|\bsii\b|\btmi\b|epson|morioka|hattori/i;
const SWISS_AND_MIYOTA = /\beta\b|sellita|miyota/i;

/**
 * Suggested terms for a manufacturer, or null when there is no sensible
 * default. Suggestions only — the audit lists them and James applies them.
 */
export function suggestedRevisionTerms(m: { id: string; name?: string; displayName?: string }): RevisionTerms | null {
  const text = `${m.id} ${m.name ?? ''} ${m.displayName ?? ''}`;
  // Orient is Epson-owned, but its F-series naming does not use Revision Codes.
  if (/orient/i.test(text)) return null;
  if (SEIKO.test(text)) return REVISION_TERM_PRESETS[1];
  if (SWISS_AND_MIYOTA.test(text)) return DEFAULT_REVISION_TERMS;
  return null;
}
